import React, { useEffect, useRef, useState } from 'react';
import { AnimationOnScroll } from 'react-animation-on-scroll';
import '../App.css';
import '../style.css';
import { useArticleImageGallery } from './ArticleImageGallery';

interface ArticleImageLayoutProps {
  images: string[];
  className?: string;
}

const ArticleImageLayout: React.FC<ArticleImageLayoutProps> = ({
  images,
  className = '',
}) => {
  const [ratios, setRatios] = useState<Record<number, number>>({});
  const containerRefs = useRef<Array<HTMLDivElement | null>>([]);
  const gallery = useArticleImageGallery();
  const idRef = useRef(
    `gallery-${Math.random().toString(36).slice(2)}-${Date.now()}`
  );

  useEffect(() => {
    if (!gallery) return;
    const id = idRef.current;
    const getElement = (i: number) => {
      const container = containerRefs.current[i];
      return (container?.querySelector('img') as HTMLImageElement) ?? null;
    };
    gallery.register(id, images, getElement);
    return () => gallery.unregister(id);
  }, [gallery, images]);

  const handleLoad = (index: number, e: React.SyntheticEvent<HTMLImageElement>) => {
    const img = e.currentTarget;
    if (img.naturalHeight > 0) {
      setRatios((prev) => ({ ...prev, [index]: img.naturalWidth / img.naturalHeight }));
    }
  };

  const handleClick = (index: number) => {
    gallery?.open(idRef.current, index);
  };

  if (images.length === 0) {
    return null;
  }

  return (
    <AnimationOnScroll animateIn="articleFadeInUp" animateOnce={true} offset={10}>
      <div className={`article-image-layout ${className}`}>
        {images.map((image, index) => {
          const ratio = ratios[index] ?? 1;
          return (
            <div
              key={index}
              ref={(el) => {
                containerRefs.current[index] = el;
              }}
              className="article-image-container"
              style={{ flexGrow: ratio, aspectRatio: ratio }}
              onClick={() => handleClick(index)}
            >
              <img
                src={image}
                alt={`Article image ${index + 1}`}
                className="article-image"
                onLoad={(e) => handleLoad(index, e)}
              />
            </div>
          );
        })}
      </div>
    </AnimationOnScroll>
  );
};

export default ArticleImageLayout;
